import { GroupWithUsers } from '@app/core/types/users';
import { useQueryParam } from '@mi-front/core';
import { LinkItem } from '@mi-front/ui';
import { MessageDescriptor, defineMessage, useIntl } from 'react-intl';

export enum TabKey {
  INFO = 'info',
  PERMISSIONS = 'permissions',
  ACTIONS_PERMISSIONS = 'actions-permissions',
}

const tabMessages = {
  info: defineMessage({
    id: 'admin.groups.viewScreen.basicInfoTab',
    defaultMessage: 'Info',
  }),
  stationsPermissions: defineMessage({
    id: 'admin.groups.viewScreen.stationsPermissionsTab',
    defaultMessage: 'Acceso a emisoras',
  }),
  actionsPermissions: defineMessage({
    id: 'admin.groups.viewScreen.actionsPermissionsTab',
    defaultMessage: 'Permisos',
  }),
};

/**
 * Builds header nav link items for the group view page.
 */
export function buildAdminGroupsViewTabs(
  group: GroupWithUsers,
  $t: (descriptor: MessageDescriptor) => string,
): LinkItem[] {
  const basePath = `/admin/groups/${group.id}`;

  return [
    {
      to: basePath,
      text: $t(tabMessages.info),
      isActive: (_match, location) => {
        const tab = new URLSearchParams(location.search).get('tab');
        return !tab || tab === TabKey.INFO;
      },
    },
    {
      to: `${basePath}?tab=${TabKey.PERMISSIONS}`,
      text: $t(tabMessages.stationsPermissions),
      isActive: (_match, location) =>
        new URLSearchParams(location.search).get('tab') === TabKey.PERMISSIONS,
    },
    {
      to: `${basePath}?tab=${TabKey.ACTIONS_PERMISSIONS}`,
      text: $t(tabMessages.actionsPermissions),
      isActive: (_match, location) =>
        new URLSearchParams(location.search).get('tab') === TabKey.ACTIONS_PERMISSIONS,
    },
  ];
}

/**
 * Builds header nav tabs for the group view and resolves the active tab from `?tab=`.
 */
export function useAdminGroupsViewTabs(group: GroupWithUsers): {
  tabs: LinkItem[];
  activeTab: TabKey;
} {
  const { $t } = useIntl();
  const tabParam = useQueryParam('tab');
  const tabs = buildAdminGroupsViewTabs(group, $t);
  const availableTabs = [TabKey.INFO, TabKey.PERMISSIONS, TabKey.ACTIONS_PERMISSIONS];
  const activeTab = availableTabs.includes(tabParam as TabKey) ? (tabParam as TabKey) : TabKey.INFO;

  return { tabs, activeTab };
}
